import React from 'react';
import { useAppContext } from '../contexts/AppContext';
import { isImageFile, normalizeImageForBook, checkPhotoQuality } from '../utils/imageHelpers';
import ConfirmModal from '../components/ConfirmModal';
import MemoryCareComingSoon from '../components/MemoryCareComingSoon';

const StWritePage = () => {
  const {
    setActiveTab, showToast,
    // Write state
    stWriteTitle, setStWriteTitle,
    stWriteBody, setStWriteBody,
    setStWritePhotoFile,
    stWritePhotoPreview, setStWritePhotoPreview,
    stWritePolishing, stWriteSaving, stWriteSavingPhase, handleStWritePolish, handleStSaveWritten,
    photoQualityWarning, setPhotoQualityWarning,
    // Enhancement nudge
    accountSettings,
    stPhotoEnhanceSkipped, setStPhotoEnhanceSkipped,
    stEnhanceAfterSave, setStEnhanceAfterSave,
    // Recording crosslink
    setStSelectedPrompt, setStInterviewMode,
    setStoryAudioBlob, setStoryAudioUrl,
    setStPostRecordStep, setStAutoTranscribeTriggered,
    startStoryRecording,
    storyVideoMode, setStoryVideoMode,
    // Memory crosslink
    setMemoryDementiaMode, setMemoryDayQuality, setMemoryTopic,
    setMemoryTab, setMemoryStep,
    // Memory Care coming-soon gate (F-12)
    MEMORY_CARE_ENABLED, submitMemoryCareInterest,
    // User
    user,
  } = useAppContext();

  const [showLeaveConfirm, setShowLeaveConfirm] = React.useState(false);

  const navigateBack = () => {
    setActiveTab('st-record');
    setStWriteTitle('');
    setStWriteBody('');
    setStWritePhotoFile(null);
    setStWritePhotoPreview(null);
    try { localStorage.removeItem(`remnant_draft_${user?.userId || user?.username}`); } catch(e) {}
  };

  const handleBack = () => {
    if ((stWriteBody || '').trim().length > 0) {
      setShowLeaveConfirm(true);
    } else {
      navigateBack();
    }
  };

  // Auto-save draft to localStorage
  React.useEffect(() => {
    const uid = user?.userId || user?.username;
    if (!uid) return;
    const body = (stWriteBody || '').trim();
    try {
      if (body) {
        localStorage.setItem(`remnant_draft_${uid}`, JSON.stringify({ title: stWriteTitle, body: stWriteBody, savedAt: new Date().toISOString() }));
      } else {
        localStorage.removeItem(`remnant_draft_${uid}`);
      }
    } catch(e) {}
  }, [stWriteBody, stWriteTitle, user]);

  // Restore draft on mount
  const [draftRestore, setDraftRestore] = React.useState(null);
  React.useEffect(() => {
    const uid = user?.userId || user?.username;
    if (!uid) return;
    // Only offer restore if the write form is currently empty
    if ((stWriteBody || '').trim()) return;
    try {
      const saved = localStorage.getItem(`remnant_draft_${uid}`);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.body?.trim()) setDraftRestore(draft);
      }
    } catch(e) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="storyteller-content">
      <button onClick={handleBack} className="st-back">← Back</button>
      <h2 className="st-h2" style={{ fontSize: '1.8rem' }}>Write a Story</h2>
      <p className="st-sub" style={{ marginBottom: 20 }}>Type a memory, rough notes, or bullet points. We can clean it up into a polished chapter.</p>

      {draftRestore && (
        <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.9rem', color: '#3c4653' }}>You have an unsaved draft.</span>
          <button onClick={() => { setStWriteTitle(draftRestore.title || ''); setStWriteBody(draftRestore.body || ''); setDraftRestore(null); }} style={{ color: '#7c2d12', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer', fontFamily: "'Lora', serif" }}>Continue draft</button>
          <button onClick={() => { setDraftRestore(null); try { localStorage.removeItem(`remnant_draft_${user?.userId || user?.username}`); } catch(e) {} }} style={{ color: '#3c4653', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif" }}>Discard</button>
        </div>
      )}

      {showLeaveConfirm && (
        <ConfirmModal
          title="Leave without saving?"
          message="Your story hasn't been saved yet. If you leave now, your work will be lost."
          confirmLabel="Leave"
          cancelLabel="Stay and save"
          onConfirm={() => { setShowLeaveConfirm(false); navigateBack(); }}
          onCancel={() => setShowLeaveConfirm(false)}
        />
      )}

      {/* Record option — voice or video */}
      <div style={{ textAlign: 'center', marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(15,23,42,0.07)' }}>
        <button onClick={() => { setStSelectedPrompt({ text: stWriteTitle.trim() || 'My Story', emoji: '' }); setActiveTab('st-record'); setStInterviewMode(false); setStoryAudioBlob(null); setStoryAudioUrl(''); setStPostRecordStep(null); setStAutoTranscribeTriggered(false); startStoryRecording(); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '12px 24px', background: 'rgba(15,23,42,0.05)', border: '2px solid rgba(15,23,42,0.12)', borderRadius: 12, cursor: 'pointer', fontSize: '1.05rem', color: '#0f172a', fontFamily: "'Lora', serif", fontWeight: 500 }}>
          Or record {storyVideoMode ? 'video' : 'voice'} instead
        </button>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10, fontSize: '0.85rem' }}>
          <button onClick={() => setStoryVideoMode(false)}
            style={{ padding: '4px 12px', fontFamily: "'Lora', serif", background: 'transparent', color: !storyVideoMode ? '#0f172a' : 'rgba(15,23,42,0.45)', border: 'none', cursor: 'pointer', fontWeight: !storyVideoMode ? 600 : 400, textDecoration: !storyVideoMode ? 'underline' : 'none', textUnderlineOffset: 4 }}>
            Voice
          </button>
          <span style={{ color: 'rgba(15,23,42,0.2)', alignSelf: 'center' }}>·</span>
          <button onClick={() => setStoryVideoMode(true)}
            style={{ padding: '4px 12px', fontFamily: "'Lora', serif", background: 'transparent', color: storyVideoMode ? '#0f172a' : 'rgba(15,23,42,0.45)', border: 'none', cursor: 'pointer', fontWeight: storyVideoMode ? 600 : 400, textDecoration: storyVideoMode ? 'underline' : 'none', textUnderlineOffset: 4 }}>
            Video
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label className="st-label">Title</label>
        <input className="st-input" value={stWriteTitle} onChange={e => setStWriteTitle(e.target.value)} placeholder="e.g. The Summer We Moved to Ohio" />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label className="st-label">Your story</label>
        <textarea className="st-input st-textarea" rows={12} value={stWriteBody} onChange={e => setStWriteBody(e.target.value)} placeholder="Write what comes to mind... bullet points, rough notes, or full sentences — anything works." style={{ minHeight: 260 }} />
        <p style={{ fontSize: '1rem', color: '#3c4653', marginTop: 6 }}>{(stWriteBody || '').split(/\s+/).filter(Boolean).length} words</p>
      </div>

      {/* Photo upload */}
      <div style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.08)' }}>
        <label className="st-label" style={{ marginBottom: 10, display: 'block' }}> Add a photo <span style={{ fontWeight: 400, color: '#3c4653' }}>(optional — you can always add one later)</span></label>
        {stWritePhotoPreview ? (
          <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img src={stWritePhotoPreview} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)' }} />
            <div>
              <p style={{ fontSize: '1rem', color: '#0f172a', marginBottom: 4 }}>Photo attached ✓</p>
              <button onClick={() => { setStWritePhotoFile(null); setStWritePhotoPreview(null); setPhotoQualityWarning(null); setStPhotoEnhanceSkipped(false); setStEnhanceAfterSave(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.95rem', color: '#c0392b', padding: 0, fontFamily: "'Lora', serif" }}>Remove</button>
            </div>
          </div>
          {photoQualityWarning && (
            <div style={{
              marginTop: 10, padding: '10px 14px', borderRadius: 8,
              background: photoQualityWarning.level === 'low' ? 'rgba(192,57,43,0.06)' : 'rgba(220,160,60,0.06)',
              border: `1px solid ${photoQualityWarning.level === 'low' ? 'rgba(192,57,43,0.2)' : 'rgba(220,160,60,0.2)'}`,
              fontSize: '0.9rem', color: photoQualityWarning.level === 'low' ? '#8b2c1a' : '#8a6d20', lineHeight: 1.5,
            }}>
              {photoQualityWarning.message}
            </div>
          )}
          {/* Enhancement nudge */}
          {(() => {
            const freeRemaining = Math.max(0, 3 - (accountSettings?.enhancementsUsed || 0));
            const hasFree = freeRemaining > 0;
            if (stEnhanceAfterSave || stPhotoEnhanceSkipped || !hasFree) return null;
            return (
              <div style={{
                marginTop: '12px', padding: '14px 16px', borderRadius: '12px',
                background: 'rgba(201, 168, 76, 0.08)', border: '1px solid rgba(201, 168, 76, 0.3)',
                display: 'flex', alignItems: 'flex-start', gap: '12px',
              }}>
                <span style={{ fontSize: '1.4rem' }}></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0f172a', marginBottom: '4px' }}>
                    Enhance this photo before printing?
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'rgba(15,23,42,0.6)', marginBottom: '10px' }}>
                    AI sharpens faces, restores faded colors, and upscales to 300 DPI for print. Takes ~30 seconds.
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setStEnhanceAfterSave(true)} style={{
                      padding: '7px 16px', borderRadius: '20px', background: '#7c2d12', color: 'white',
                      border: 'none', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                    }}>
                      Enhance for Free
                    </button>
                    <button onClick={() => setStPhotoEnhanceSkipped(true)} style={{
                      padding: '7px 16px', borderRadius: '20px', background: 'transparent',
                      color: 'rgba(15,23,42,0.5)', border: '1px solid rgba(15,23,42,0.15)',
                      fontSize: '0.82rem', cursor: 'pointer',
                    }}>
                      Keep as-is
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
          </>
        ) : (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '10px 20px', background: 'rgba(15,23,42,0.06)', borderRadius: 8, border: '1px solid rgba(15,23,42,0.12)', fontSize: '1rem', color: '#0f172a', fontFamily: "'Lora', serif" }}>
             Choose photo
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (!isImageFile(file)) { showToast('Please choose an image file.', 'error'); return; }
              if (file.size > 10 * 1024 * 1024) { showToast('Image must be under 10 MB.', 'error'); return; }
              try {
                const normalizedFile = await normalizeImageForBook(file);
                const preview = URL.createObjectURL(normalizedFile);
                setStWritePhotoFile(normalizedFile);
                setStWritePhotoPreview(preview);
                // Check photo quality for print
                const quality = await checkPhotoQuality(normalizedFile, 'photo-spread');
                setPhotoQualityWarning(quality.level !== 'good' ? quality : null);
              } catch (err) { showToast('Could not load photo.', 'error'); }
            }} />
          </label>
        )}
      </div>

      {/* 3-tier cleanup — visible when there's text */}
      {stWriteBody.trim() && (
        <div style={{ background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.07)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
          <p style={{ fontSize: '0.95rem', color: '#3c4653', textAlign: 'center', marginBottom: 2, fontFamily: "'Lora', serif" }}>{stWritePolishing ? 'Working on it...' : 'How much help do you want?'}</p>
          {!stWritePolishing && <p style={{ fontSize: '0.88rem', color: '#3c4653', textAlign: 'center', marginBottom: 10, fontStyle: 'italic' }}>Light cleanup or a full rewrite. Your voice stays either way.</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => handleStWritePolish('cleanup')} className="st-btn-secondary" style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, fontSize: '0.95rem', lineHeight: 1.25, padding: '10px 12px', borderColor: 'rgba(15,23,42,0.25)' }} disabled={stWritePolishing}>
              <span>🦊 A Flick of the Tail</span>
              <span style={{ fontSize: '0.78rem', color: 'rgba(15,23,42,0.55)', fontWeight: 400 }}>Tidied up, in your own words</span>
            </button>
            <button onClick={() => handleStWritePolish('chapter')} className="st-btn-secondary" style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, fontSize: '0.95rem', lineHeight: 1.25, padding: '10px 12px' }} disabled={stWritePolishing}>
              <span>✨ The Whole Tale</span>
              <span style={{ fontSize: '0.78rem', color: 'rgba(15,23,42,0.55)', fontWeight: 400 }}>Beautifully written</span>
            </button>
          </div>
        </div>
      )}

      <button onClick={handleStSaveWritten} className="st-btn-primary" style={{ width: '100%' }} disabled={!stWriteTitle.trim() || !stWriteBody.trim() || stWriteSaving}>
        {stWriteSaving
          ? (
              stWriteSavingPhase >= 3 ? 'Slow connection — still working…'
              : stWriteSavingPhase === 2 ? (stWritePhotoPreview ? 'Almost there — your photo is on its way…' : 'Almost there…')
              : stWriteSavingPhase === 1 ? (stWritePhotoPreview ? 'Uploading your photo…' : 'Saving…')
              : 'Saving…'
            )
          : '✓ Save & Add to Book'}
      </button>

      {/* Crosslink — F-12: Memory Care gated behind the coming-soon placeholder + interest sensor. */}
      <div style={{ borderTop: '1px solid rgba(15,23,42,0.05)', paddingTop: 20, marginTop: 28, textAlign: 'center' }}>
        {MEMORY_CARE_ENABLED ? (
          <>
            <p style={{ fontSize: '1.05rem', color: '#3c4653', marginBottom: 12 }}>Want to capture someone else's story instead?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { setMemoryDementiaMode(false); setMemoryTab('interview'); setMemoryStep('intro'); setActiveTab('memory'); }}
                style={{ background: 'none', border: '1.5px solid rgba(15,23,42,0.08)', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
                 Interview Someone
              </button>
              <button onClick={() => { setMemoryDementiaMode(true); setMemoryDayQuality(null); setMemoryTopic(null); setMemoryTab('interview'); setMemoryStep('intro'); setActiveTab('memory'); }}
                style={{ background: 'rgba(140,80,180,0.03)', border: '1.5px solid rgba(140,80,180,0.12)', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
                 Memory Care
              </button>
            </div>
          </>
        ) : (
          <MemoryCareComingSoon variant="inline" onInterest={submitMemoryCareInterest} />
        )}
      </div>
    </div>
  );
};

export default StWritePage;
